import { FC, PropsWithChildren } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import { MapType, MapWithSelection } from '@/features/map';
import { Typography } from '@mui/material';
import { Trans, useTranslation } from 'react-i18next';
import { Placeholder } from './Placeholder';
import { CreateAdFormData } from '@/pages/ad-pages/CreateAd';
import { Control } from 'react-hook-form';
import { MapWithDevices } from '@/features/map/MapWithDevices';
import { MapLayerMouseEvent } from 'mapbox-gl';
import { resetZones } from '@/features/ad/adSlice';
import { useDispatch } from 'react-redux';
import {
  Country,
  Area,
  useGetAreasQuery,
  useGetZonesQuery,
} from '@/models/location';
import { Button } from '@/components/Button';
import { Card } from '@/features/card';
import { useGetCountriesQuery } from '@/models/location/query';
import {
  FALLBACK_AREA_ID,
  FALLBACK_COUNTRY_ID,
} from '@/features/location/constants';

type Props = {
  control?: Control<CreateAdFormData>;
  selectedZones?: number[];
  areaId?: number;
  countryId?: number;
  onZonesSelect?: (zones: number[]) => void;
  onChangeArea: (id?: number) => void;
  onChangeCountry: (id?: number) => void;
  mapType: MapType;
};

export const LocationSelector: FC<PropsWithChildren<Props>> = ({
  selectedZones = [],
  onZonesSelect,
  areaId,
  countryId,
  onChangeArea,
  onChangeCountry,
  mapType,
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { data: countries } = useGetCountriesQuery();
  const { data: areasData } = useGetAreasQuery(
    { countryId: countryId || FALLBACK_COUNTRY_ID },
    {
      skip: !countryId,
    }
  );
  const { data: zonesData, isLoading } = useGetZonesQuery(
    {
      areaId: areaId || FALLBACK_AREA_ID,
    },
    {
      skip: !areaId,
    }
  );

  const onZoneSelect = (e: MapLayerMouseEvent) => {
    if (!onZonesSelect) return;

    const id =
      e?.features && e?.features?.length > 0
        ? e?.features[0].properties?.id
        : null;

    if (id) {
      let newSelection: number[] = [];

      if (selectedZones.includes(id)) {
        newSelection = selectedZones.filter((it) => it !== id);
      } else {
        newSelection = [...selectedZones, id];
      }

      onZonesSelect(newSelection);
    }
  };

  const handleChangeArea = async (
    _: React.SyntheticEvent,
    value: Area | null
  ) => {
    onChangeArea(value ? value.id : undefined);
  };

  const handleSelectAllZones = () => {
    if (!onZonesSelect) return;

    const zonesIds = zonesData?.map((it: { id: any }) => it.id) || [];
    onZonesSelect(selectedZones.length > 0 ? [] : zonesIds);
  };

  const handleChangeCountry = (_: any, value: Country | null) => {
    onChangeCountry(value ? value.id : undefined);
    onChangeArea(undefined);
    dispatch(resetZones());
  };

  return (
    <div className="flex flex-col gap-2">
      <Typography variant="h6" sx={{ mb: '8px' }}>
        <Trans i18nKey="location_selector_section.title" />:
      </Typography>
      <Card className="mb-4">
        <div className="md:flex mt-2">
          {isLoading ? (
            <Placeholder />
          ) : (
            <div className="grid grid-cols-2 w-full gap-4">
              <Autocomplete
                id="country"
                className="md:flex-1"
                value={countries?.find((it) => it.id === countryId)}
                onChange={handleChangeCountry}
                options={countries || []}
                autoHighlight
                getOptionLabel={(option) => t(`country.${option.name}`)}
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    {t(`country.${option.name}`)}
                  </Box>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t(
                      'location_selector_section.country.autocomplete.label'
                    )}
                    inputProps={{
                      ...params.inputProps,
                      autoComplete: 'off',
                    }}
                  />
                )}
              />
              <Autocomplete
                id="area"
                className="md:flex-1"
                value={areasData?.find((it) => it.id === areaId)}
                onChange={handleChangeArea}
                options={areasData || []}
                autoHighlight
                getOptionLabel={(option) => t(`area.${option.name}`)}
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    {t(`area.${option.name}`)}
                  </Box>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t(
                      'location_selector_section.area.autocomplete.label'
                    )}
                    inputProps={{
                      ...params.inputProps,
                      autoComplete: 'off',
                    }}
                  />
                )}
              />
            </div>
          )}
        </div>
      </Card>

      <div className="relative">
        {mapType === MapType.WithSelection && (
          <div className="absolute z-10 top-4 left-4">
            <Button
              variant="contained"
              onClick={handleSelectAllZones}
              disableElevation
            >
              <Trans
                i18nKey={
                  selectedZones.length > 0
                    ? 'location_selector_section.map.deselect_all_zones_button_label'
                    : 'location_selector_section.map.select_all_zones_button_label'
                }
              />
            </Button>
          </div>
        )}

        {mapType === MapType.WithSelection && (
          <MapWithSelection
            country={countries?.find((it) => it.id === countryId)}
            area={areasData?.find((it) => it.id === areaId)}
            zones={zonesData}
            onZoneSelect={onZoneSelect}
            selectedZones={selectedZones}
            height="50vh"
          />
        )}

        {mapType === MapType.WithDevices && (
          <MapWithDevices
            country={countries?.find((it) => it.id === countryId)}
            area={areasData?.find((it) => it.id === areaId)}
            zones={zonesData}
            height="50vh"
          />
        )}
      </div>
    </div>
  );
};
